import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    ArrowRight,
    DollarSign,
    Sliders,
    Lock,
    Droplets,
    ShieldCheck,
    FileText,
    Upload,
    ClipboardCheck,
    Sparkles,
    Phone,
    Mail,
    MapPin,
    CalendarCheck,
    TrendingUp,
    Unlock,
    CheckCircle2,
} from "lucide-react";
import { useBranding } from "../lib/branding";
import { reviewPath } from "../lib/links";

const HERO = "https://images.unsplash.com/photo-1695653422557-3b85c1d6d061?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxvbGRlciUyMGNvdXBsZSUyMGZpbmFuY2lhbCUyMGFkdmlzb3J8ZW58MHx8fHwxNzc3OTQxOTA1fDA&ixlib=rb-4.1.0&q=85";
const ARCH = "https://images.unsplash.com/photo-1618858553936-84170aa4be58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG5hdnklMjBibHVlJTIwd2hpdGUlMjB0ZXh0dXJlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3Nzk0MTkxMnww&ixlib=rb-4.1.0&q=85";

const why = [
    { icon: DollarSign, title: "Hidden fees and costs", text: "Many contracts hide layered fees that quietly erode your returns year after year." },
    { icon: Sliders, title: "Income rider structure", text: "Riders sound great on paper. The fine print determines whether they actually work for you." },
    { icon: Lock, title: "Surrender charges", text: "Most owners don't know how long their money is locked up — or what it costs to access it." },
    { icon: Droplets, title: "Liquidity limitations", text: "Free withdrawal provisions vary widely. Knowing yours is critical for retirement planning." },
    { icon: ShieldCheck, title: "Real guarantees vs assumptions", text: "What's contractually guaranteed and what's a hypothetical illustration? They are not the same." },
    { icon: TrendingUp, title: "Your crediting strategy", text: "Annual point-to-point, monthly sum, monthly average — each calculates your gain very differently. Add S&P 500 vs. proprietary volatility-controlled indices, plus caps, spreads, and participation rates, and two contracts with the same balance can credit wildly different amounts." },
];

const deliverables = [
    {
        icon: FileText,
        title: "Free PDF annuity summary — yours to keep",
        text: "When your review is complete you receive a no-obligation PDF printout that summarizes your contract — type, carrier, current value, surrender schedule, income rider, and crediting strategy — to file away and reference any time. No strings. Yours forever.",
    },
    {
        icon: Phone,
        title: "Optional 3-way call with your carrier",
        text: "Want the most precise figures? We'll do a 3-way call with your annuity provider together so you hear the exact contract details straight from the source — current values, surrender amounts, rider rollups, the works. Completely free. No obligation. Just the facts on record.",
    },
    {
        icon: ShieldCheck,
        title: "Zero sales pressure — ever",
        text: "This is not a pitch. We will never push a product. Some annuities are great as-is — we'll tell you when that's the case. No follow-up sales calls unless you specifically ask. This is clarification, not a sales process.",
    },
];

const steps = [
    { icon: FileText, title: "Enter Your Annuity Details", text: "A short, plain-English questionnaire about the contract you own today." },
    { icon: Upload, title: "Upload Your Statement", text: "Share your most recent annuity statement (PDF or photo). Optional, but recommended." },
    { icon: ClipboardCheck, title: "Get Your Personalized Review", text: "Receive a clear scorecard and book a 15-minute call to walk through it together." },
];

const types = [
    {
        title: "Fixed Annuities",
        text: "Predictable, fixed-rate growth. Often used for principal protection in retirement portfolios.",
        bullets: ["Stated interest rate", "Principal protection", "Predictable income options"],
    },
    {
        title: "Fixed Indexed Annuities",
        text: "Growth tied to a market index with downside protection. Popular but frequently misunderstood.",
        bullets: ["Index-linked growth", "Floor against losses", "Caps, spreads, participation rates"],
    },
    {
        title: "Variable Annuities",
        text: "Subaccount investments with market exposure. Many owners believed they couldn't lose money. They can.",
        bullets: ["Market-based returns", "Exposed to losses", "Often higher fee structures"],
    },
];

export default function Home() {
    const { branding } = useBranding();
    const showOwner = branding?.show_owner_section !== false && !!branding?.owner_name;
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-navy-900" data-testid="hero-section">
                <div className="absolute inset-0">
                    <img src={HERO} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/70" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent" />
                    <div className="absolute -top-24 -right-32 w-[520px] h-[520px] rounded-full bg-amber-400/15 blur-3xl" />
                    <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-emerald-400/15 blur-3xl" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-24 md:py-36 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <div className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-xs font-bold text-amber-300 mb-6 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                            <Sparkles className="w-3.5 h-3.5" /> Reviews · New Purchases · No Sales Pressure
                        </div>
                        <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                            Stop Guessing About<br className="hidden sm:block" /> <span className="text-amber-300">Your Annuity.</span>
                        </h1>
                        <p className="mt-8 text-lg sm:text-xl text-blue-50/90 max-w-2xl leading-relaxed">
                            Get a clear, simple breakdown of what you own — or guidance before you buy your first or next annuity. Either way: no sales pressure, no fees.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <Link to={reviewPath()} data-testid="hero-cta-review">
                                <Button className="bg-amber-400 text-slate-900 hover:bg-amber-300 hover:-translate-y-0.5 transition-all px-8 py-7 text-base font-bold rounded-md w-full sm:w-auto shadow-lg shadow-amber-500/25">
                                    Get My Free Annuity Review
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                            <a href="#how-it-works" data-testid="hero-cta-how">
                                <Button variant="outline" className="bg-white/5 backdrop-blur border-white/40 text-white hover:bg-white/15 hover:text-white px-8 py-7 text-base font-medium rounded-md w-full sm:w-auto">
                                    How It Works
                                </Button>
                            </a>
                        </div>
                        <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4 text-sm text-blue-100/90">
                            <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-md bg-emerald-400/20 text-emerald-300 flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></span> Licensed insurance professional</div>
                            <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-md bg-amber-400/20 text-amber-300 flex items-center justify-center"><Sparkles className="w-4 h-4" /></span> 100% free · no obligation</div>
                            <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-md bg-sky-400/20 text-sky-300 flex items-center justify-center"><FileText className="w-4 h-4" /></span> Confidential & secure</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* OWNER / FEATURED ADVISOR */}
            {showOwner && (
                <section className="bg-white border-b border-slate-100 py-12 md:py-16" data-testid="owner-section">
                    <div className="max-w-6xl mx-auto px-6 sm:px-8">
                        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                            <div className="md:col-span-4 flex justify-center md:justify-start">
                                {branding.owner_photo_url ? (
                                    <img
                                        src={branding.owner_photo_url}
                                        alt={branding.owner_name}
                                        className="w-52 md:w-60 aspect-[3/4] rounded-2xl object-cover object-top border border-slate-200 shadow-lg"
                                        data-testid="owner-photo"
                                    />
                                ) : (
                                    <div className="w-52 md:w-60 aspect-[3/4] rounded-2xl bg-navy-900 text-white flex items-center justify-center font-serif text-7xl border border-slate-200 shadow-lg">
                                        {(branding.owner_name || "?").slice(0, 1)}
                                    </div>
                                )}
                            </div>
                            <div className="md:col-span-8">
                                <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-2" data-testid="owner-title">
                                    {branding.owner_title || "Licensed Insurance Professional"}
                                </div>
                                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight" data-testid="owner-name">
                                    Hi, I'm {branding.owner_name.split(" ")[0]}.
                                </h2>
                                {branding.owner_bio && (
                                    <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="owner-bio">
                                        {branding.owner_bio}
                                    </p>
                                )}
                                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-700">
                                    {branding.owner_phone && (
                                        <a href={`tel:${branding.owner_phone}`} className="inline-flex items-center gap-2 hover:text-navy-900" data-testid="owner-phone">
                                            <Phone className="w-4 h-4 text-navy-900" /> {branding.owner_phone}
                                        </a>
                                    )}
                                    {branding.owner_email && (
                                        <a href={`mailto:${branding.owner_email}`} className="inline-flex items-center gap-2 hover:text-navy-900" data-testid="owner-email">
                                            <Mail className="w-4 h-4 text-navy-900" /> {branding.owner_email}
                                        </a>
                                    )}
                                    {branding.owner_states && (
                                        <div className="inline-flex items-center gap-2" data-testid="owner-states">
                                            <MapPin className="w-4 h-4 text-navy-900" /> Licensed in {branding.owner_states}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                    <Link to={reviewPath()} data-testid="owner-cta-review">
                                        <Button className="bg-navy-900 hover:bg-navy-800 text-white px-8 py-7 rounded-md font-semibold w-full sm:w-auto">
                                            Get My Free Annuity Review <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </Link>
                                    {branding.owner_calendly_link && (
                                        <a href={branding.owner_calendly_link} target="_blank" rel="noreferrer" data-testid="owner-cta-calendly">
                                            <Button variant="outline" className="px-8 py-7 rounded-md w-full sm:w-auto">
                                                <CalendarCheck className="w-4 h-4 mr-2" /> Book a Call
                                            </Button>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* TRUST */}
            <section className="py-20 md:py-28 bg-white" data-testid="trust-section">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
                    <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-6">Why this exists</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                        You don't need another sales pitch. You need clarity.
                    </h2>
                    <p className="mt-8 text-lg sm:text-xl text-slate-600 leading-relaxed">
                        Some annuities are valuable. Some are outdated. Most are misunderstood.
                        We help you understand what you own before making any decisions.
                    </p>
                </div>
            </section>

            {/* WHAT YOU GET */}
            <section className="py-20 md:py-28 bg-slate-50" data-testid="deliverables-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-4">What you get — completely free</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            A free, no-pressure clarification of what you actually own.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">No fee. No commitment. No follow-up sales calls. Just clear answers and documentation you can keep.</p>
                    </div>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {deliverables.map((d, i) => {
                            const Icon = d.icon;
                            const tones = [
                                "bg-emerald-500 text-white",
                                "bg-amber-400 text-slate-900",
                                "bg-sky-500 text-white",
                            ];
                            return (
                                <div
                                    key={d.title}
                                    data-testid={`deliverable-card-${i}`}
                                    className="bg-white border border-slate-200 rounded-xl p-8 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                                >
                                    <div className={`w-12 h-12 rounded-md flex items-center justify-center shadow-md ${tones[i % tones.length]}`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="mt-6 font-serif text-2xl text-navy-900 leading-tight">{d.title}</h3>
                                    <p className="mt-3 text-base text-slate-600 leading-relaxed">{d.text}</p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-12 max-w-3xl bg-gradient-to-br from-navy-900 via-navy-900 to-emerald-900 text-white rounded-2xl p-6 sm:p-8 text-center border border-amber-400/20 shadow-xl">
                        <div className="uppercase tracking-[0.25em] text-[11px] font-bold text-amber-300 mb-2">Our promise</div>
                        <p className="font-serif text-xl sm:text-2xl leading-snug">
                            "You will never feel pressured here. If your contract is right for you, we'll tell you. If something is worth a closer look, we'll explain why — in plain English. The choice is always yours."
                        </p>
                    </div>
                </div>
            </section>

            {/* OUR NETWORK */}
            <section className="py-20 md:py-28 bg-white border-t border-slate-100" data-testid="network-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="grid lg:grid-cols-5 gap-12 items-start">
                        <div className="lg:col-span-2">
                            <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-4">Who reviews your contract</div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                                A nationwide network of specialists who read the fine print for a living.
                            </h2>
                        </div>
                        <div className="lg:col-span-3 space-y-6">
                            <p className="text-lg text-slate-700 leading-relaxed">
                                Every advisor in our network has built their practice around one quiet truth:
                                <span className="text-navy-900 font-semibold"> most annuity owners were never given a clear explanation of what they actually bought.</span>
                            </p>
                            <p className="text-lg text-slate-600 leading-relaxed">
                                Our team spends their days inside the contracts others skim over — older policies, misunderstood income
                                riders, forgotten bonuses, surrender schedules buried on page 14. That narrow focus is exactly why you
                                get real answers here, not a script.
                            </p>
                            <p className="text-lg text-slate-700 leading-relaxed">
                                You walk away with one of two things: <span className="text-navy-900 font-semibold">honest confirmation that your annuity is already working for you</span>,
                                or a clear, written path to make it work harder. Either way, you leave with clarity — and that's the
                                only outcome we work toward.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-4 pt-4">
                                {[
                                    { k: "Licensed", v: "Independently licensed, state-regulated advisors" },
                                    { k: "Focused", v: "Practices built specifically around annuity reviews" },
                                    { k: "Independent", v: "Not tied to any single carrier or product" },
                                    { k: "No-pressure", v: "No fees, no obligations, no follow-up sales calls" },
                                ].map((t) => (
                                    <div key={t.k} className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid={`network-trust-${t.k.toLowerCase()}`}>
                                        <ShieldCheck className="w-5 h-5 text-navy-900 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-navy-900">{t.k}</div>
                                            <div className="mt-1 text-sm text-slate-600 leading-snug">{t.v}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHY IT MATTERS */}
            <section className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white" data-testid="why-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-bold text-emerald-700 mb-4">What we look at</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            Six things every annuity owner should understand.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {why.map((w, i) => {
                            const Icon = w.icon;
                            const tones = [
                                { ring: "ring-rose-100", icon: "bg-rose-100 text-rose-700" },
                                { ring: "ring-amber-100", icon: "bg-amber-100 text-amber-700" },
                                { ring: "ring-sky-100", icon: "bg-sky-100 text-sky-700" },
                                { ring: "ring-emerald-100", icon: "bg-emerald-100 text-emerald-700" },
                                { ring: "ring-violet-100", icon: "bg-violet-100 text-violet-700" },
                                { ring: "ring-orange-100", icon: "bg-orange-100 text-orange-700" },
                            ];
                            const t = tones[i % tones.length];
                            return (
                                <div
                                    key={w.title}
                                    data-testid={`why-card-${i}`}
                                    className={`bg-white border border-slate-200 rounded-xl p-8 hover:shadow-xl hover:-translate-y-1 hover:ring-2 ${t.ring} transition-all`}
                                >
                                    <div className={`w-12 h-12 rounded-md flex items-center justify-center ${t.icon}`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="mt-6 font-serif text-2xl text-navy-900">{w.title}</h3>
                                    <p className="mt-3 text-base text-slate-600 leading-relaxed">{w.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* THREE WAYS WE HELP */}
            <section className="py-20 md:py-24 bg-white border-b border-slate-100" data-testid="three-ways-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-amber-700 mb-4">How we can help</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            Whether you already own an annuity or you're just looking — start here.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">Three ways we can help. All free. All conflict-free.</p>
                    </div>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="way-existing">
                            <div className="w-12 h-12 rounded-md bg-emerald-500 text-white flex items-center justify-center shadow-md">
                                <ShieldCheck className="w-6 h-6" />
                            </div>
                            <div className="mt-5 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1">Already own one</div>
                            <h3 className="mt-4 font-serif text-2xl text-navy-900">Review an existing annuity</h3>
                            <p className="mt-3 text-slate-600 text-base leading-relaxed">
                                We break down what you own — fees, surrender charges, riders, and whether it still fits your retirement plan. You'll know exactly where you stand.
                            </p>
                            <Link to={reviewPath()} className="mt-6 inline-flex items-center text-emerald-700 font-semibold hover:text-emerald-800" data-testid="way-existing-cta">
                                Start a free review <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all relative" data-testid="way-first">
                            <span className="absolute -top-3 right-6 bg-amber-400 text-slate-900 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full shadow-md">New buyer</span>
                            <div className="w-12 h-12 rounded-md bg-amber-500 text-white flex items-center justify-center shadow-md">
                                <Sparkles className="w-6 h-6" />
                            </div>
                            <div className="mt-5 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 bg-amber-100 rounded-full px-2.5 py-1">Considering your first</div>
                            <h3 className="mt-4 font-serif text-2xl text-navy-900">Pre-purchase guidance</h3>
                            <p className="mt-3 text-slate-600 text-base leading-relaxed">
                                Thinking about your first annuity? We help you compare options, understand the fine print, and figure out if an annuity even makes sense for your situation — before you sign anything.
                            </p>
                            <Link to={reviewPath()} className="mt-6 inline-flex items-center text-amber-700 font-semibold hover:text-amber-800" data-testid="way-first-cta">
                                Get pre-purchase guidance <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                        <div className="bg-gradient-to-br from-sky-50 to-white border-2 border-sky-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="way-additional">
                            <div className="w-12 h-12 rounded-md bg-sky-500 text-white flex items-center justify-center shadow-md">
                                <TrendingUp className="w-6 h-6" />
                            </div>
                            <div className="mt-5 inline-flex items-center text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 bg-sky-100 rounded-full px-2.5 py-1">Adding to your plan</div>
                            <h3 className="mt-4 font-serif text-2xl text-navy-900">Add another annuity</h3>
                            <p className="mt-3 text-slate-600 text-base leading-relaxed">
                                Already have one and looking to add to your retirement income strategy? We review what you own first, then help you choose what genuinely complements it — not what pays the highest commission.
                            </p>
                            <Link to={reviewPath()} className="mt-6 inline-flex items-center text-sky-700 font-semibold hover:text-sky-800" data-testid="way-additional-cta">
                                Plan your next step <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* STRATEGIC USES — IRA→Roth + CD replacement */}
            <section className="py-20 md:py-24 bg-slate-50 border-b border-slate-100" data-testid="strategic-uses-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-sky-700 mb-4">Strategic uses · most people don't know about</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            Two ways an annuity might save your retirement plan thousands.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">
                            Most advisors don't bring these up because they're not the easy commission. We do — because they may be the right fit for you.
                        </p>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 gap-6">
                        <div className="bg-white border-2 border-sky-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="strategy-ira-roth">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-md bg-sky-600 text-white flex items-center justify-center shadow-md">
                                    <Unlock className="w-6 h-6" />
                                </div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700 bg-sky-100 rounded-full px-2.5 py-1">Tax strategy</div>
                            </div>
                            <h3 className="mt-5 font-serif text-2xl text-navy-900">Convert your IRA to a Roth — without losing account value</h3>
                            <p className="mt-3 text-slate-600 text-base leading-relaxed">
                                Standard Roth conversions trigger an immediate tax hit that eats into your principal. A properly structured annuity can spread the conversion across years and protect your account value — meaning you can pay the tax bill without selling shares or losing the principal you'd planned on growing.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-slate-700">
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>No loss of account value during conversion</span></li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>Tax-free growth and withdrawals afterward (Roth rules)</span></li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>Reduces required minimum distributions (RMDs) later</span></li>
                            </ul>
                            <Link to={reviewPath()} className="mt-6 inline-flex items-center text-sky-700 font-semibold hover:text-sky-800" data-testid="strategy-ira-roth-cta">
                                Ask about IRA → Roth strategy <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                        <div className="bg-white border-2 border-amber-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all" data-testid="strategy-cd-replacement">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-md bg-amber-600 text-white flex items-center justify-center shadow-md">
                                    <DollarSign className="w-6 h-6" />
                                </div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 bg-amber-100 rounded-full px-2.5 py-1">Yield strategy</div>
                            </div>
                            <h3 className="mt-5 font-serif text-2xl text-navy-900">Replace your CDs — higher rates, more access, tax deferral</h3>
                            <p className="mt-3 text-slate-600 text-base leading-relaxed">
                                If you're parking money in CDs for safety, today's fixed annuities often offer materially higher rates, partial penalty-free withdrawal access, and tax-deferred growth — meaning you keep more of every dollar you earn. Same principal protection, better economics.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-slate-700">
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>Typically 1-3% higher fixed rates than current CD yields</span></li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>10% annual penalty-free withdrawal in most contracts</span></li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /><span>Tax-deferred growth · pay tax only when you withdraw</span></li>
                            </ul>
                            <Link to={reviewPath()} className="mt-6 inline-flex items-center text-amber-700 font-semibold hover:text-amber-800" data-testid="strategy-cd-replacement-cta">
                                Compare to my current CD <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                    </div>
                    <p className="mt-10 text-center text-xs text-slate-700 max-w-2xl mx-auto">
                        These strategies depend on your tax bracket, age, RMD timeline, and existing retirement assets. We'll review the specifics with you — no obligation.
                    </p>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how-it-works" className="py-20 md:py-28 bg-slate-50" data-testid="how-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-4">How it works</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            Three straightforward steps.
                        </h2>
                        <p className="mt-6 text-lg text-slate-600">No spam. No pressure. Just a clear understanding of what you own.</p>
                    </div>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {steps.map((s, i) => {
                            const Icon = s.icon;
                            const tones = [
                                { num: "text-emerald-200", chip: "bg-emerald-500" },
                                { num: "text-amber-200", chip: "bg-amber-500" },
                                { num: "text-sky-200", chip: "bg-sky-500" },
                            ];
                            const t = tones[i % tones.length];
                            return (
                                <div key={s.title} data-testid={`step-card-${i}`} className="relative bg-white border border-slate-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-all">
                                    <div className={`absolute -top-5 -left-2 font-serif text-7xl ${t.num} leading-none select-none`}>0{i + 1}</div>
                                    <div className="relative">
                                        <div className={`w-12 h-12 rounded-md ${t.chip} text-white flex items-center justify-center shadow-md`}>
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <h3 className="mt-6 font-serif text-2xl text-navy-900">{s.title}</h3>
                                        <p className="mt-3 text-base text-slate-600 leading-relaxed">{s.text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TYPES */}
            <section className="py-20 md:py-28 bg-white" data-testid="types-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-4">Types of annuities</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 leading-tight">
                            Know what you own.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {types.map((t, i) => (
                            <div key={t.title} data-testid={`type-card-${i}`} className="bg-white border border-slate-200 rounded-xl p-8 hover:shadow-md transition-all">
                                <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700">Type 0{i + 1}</div>
                                <h3 className="mt-3 font-serif text-2xl text-navy-900">{t.title}</h3>
                                <p className="mt-4 text-base text-slate-600 leading-relaxed">{t.text}</p>
                                <ul className="mt-6 space-y-2 text-sm text-slate-700">
                                    {t.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2">
                                            <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-navy-900" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative overflow-hidden bg-navy-900 text-white" data-testid="final-cta-section">
                <div className="absolute inset-0">
                    <img src={ARCH} alt="" className="w-full h-full object-cover opacity-15" />
                    <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-900 to-navy-800" />
                </div>
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-24 md:py-32 text-center">
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
                        Know What You Own Before<br className="hidden sm:block" /> You Make a Decision
                    </h2>
                    <p className="mt-6 text-lg text-blue-100/85 max-w-2xl mx-auto">
                        A free, no-pressure review of your existing annuity contract — completed by a licensed insurance professional.
                    </p>
                    <div className="mt-10">
                        <Link to={reviewPath()} data-testid="footer-cta-review">
                            <Button className="bg-white text-navy-900 hover:bg-blue-50 hover:-translate-y-0.5 transition-transform px-10 py-7 text-base font-semibold rounded-md">
                                Start My Free Review
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
